// Naive CSS scoper — prepends `#scopeId ` to every user-supplied selector so a
// per-product CSS block can't leak styles to the rest of the page.
//
// Handles:
//   - Comma-separated selector lists (`.a, .b { … }` → `#s .a, #s .b { … }`)
//   - At-rules (`@media …`, `@keyframes …`) left untouched at the top level;
//     their nested selectors ARE still scoped.
//   - `:root`, `html`, `body` are rewritten to point at the scope root so
//     users can define block-level variables (`:root { --brand: red; }`).
//
// Good enough for the "admin authors a spec table" use-case. Not a full CSS
// parser — advanced features like `@supports` and `@container` should still
// work but nested scoping isn't optimised.
export function scopeCss(css, scopeId) {
  if (!css || !scopeId) return "";
  const prefix = `#${scopeId}`;
  // Strip comments first so they don't confuse the selector splitter.
  const src = css.replace(/\/\*[\s\S]*?\*\//g, "");
  // Replace each block-header `selectors {` with a scoped version.
  return src.replace(/([^{}]+)\{/g, (match, header) => {
    const trimmed = header.trim();
    // Leave at-rules (media/keyframes/supports/…) alone — their bodies still
    // contain nested rule blocks that will be captured by later regex passes.
    if (trimmed.startsWith("@")) return match;
    const scoped = trimmed
      .split(",")
      .map((raw) => {
        const s = raw.trim();
        if (!s) return "";
        if (s === ":root" || s === "html" || s === "body") return prefix;
        // Nested rules inside @keyframes use percent stops (`0%`, `100%`,
        // `from`, `to`) — don't scope those.
        if (/^(\d+%|from|to)$/i.test(s)) return s;
        return `${prefix} ${s}`;
      })
      .filter(Boolean)
      .join(", ");
    return `${scoped} {`;
  });
}
